import { useCallback, useMemo, useState } from 'react';

import { AutoComplete, EquipUIFireflyV2Theme, Search } from '@equip.health/ui';

import { ApiCommand } from '~/lib/Api';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { emptyPatientDiagnosis } from '~/lib/util/clinicalProfile.util';

const { primary } = EquipUIFireflyV2Theme.colors;

type DiagnosisSearchProps = {
  id: string;
  marginBottom?: number | string;
  multiple?: boolean;
  onRemove?: (suggestion: AutoCompleteOption) => void;
  placeholder?: string;
  selected: AutoCompleteOption[];
  title?: string;
  updateSelectedDiagnosis: (diagnosis: PatientDiagnosis) => void;
};

const DIAGNOSIS_SEARCH_PAGE_SIZE = 10;

const DiagnosisSearch = ({
  id,
  marginBottom,
  multiple = false,
  onRemove,
  placeholder,
  selected,
  title,
  updateSelectedDiagnosis,
}: DiagnosisSearchProps) => {
  const [diagnosisSearchResults, setDiagnosisSearchResults] = useState<
    DiagnosisCode[]
  >([]);

  const [pageNumber, setPageNumber] = useState<number>(1);
  const {
    loading: isDiagnosisCodesLoading,
    sendRequest: getDiagnosisCodesSearch,
  } = useApi<DiagnosisCode[]>();

  const diagnosisCodesOptions = useMemo<AutoCompleteOption[]>(
    () =>
      diagnosisSearchResults?.map(
        (value: DiagnosisCode): AutoCompleteOption => ({
          primaryText: `${value.shortDescription}`,
          secondaryText: [value.code],
          value: value.code,
        }),
      ) ?? [],
    [diagnosisSearchResults],
  );

  const updateDiagnosisSearchResults =
    (newPageNumber: number) =>
    (diagnosisCodes: DiagnosisCode[]): void => {
      if (diagnosisCodes) {
        setDiagnosisSearchResults((curr: DiagnosisCode[]): DiagnosisCode[] =>
          newPageNumber === 1
            ? (diagnosisCodes ?? [])
            : [...curr, ...(diagnosisCodes ?? [])],
        );
        setPageNumber(newPageNumber);
      }
    };

  const handleDiagnosisSearch = useCallback(
    (searchTerm = '', isFirstPage = true): void => {
      const newPageNumber = isFirstPage ? 1 : pageNumber + 1;

      getDiagnosisCodesSearch({
        callback: updateDiagnosisSearchResults(newPageNumber),
        command: ApiCommand.GET,
        options: {
          'is-billable': true,
          'page-number': newPageNumber,
          'page-size': DIAGNOSIS_SEARCH_PAGE_SIZE,
          'search-string': searchTerm,
        },
        url: urlConstants.clinical_data.getDiagnosisCodesSearch,
      });
    },
    [pageNumber],
  );
  const handleDiagnosisSelect = useCallback(
    (diagnosis: AutoCompleteOption): void => {
      const selectedDiagnosisObj = diagnosisSearchResults?.find(
        (value: DiagnosisCode) => value.code === diagnosis.value,
      );
      if (selectedDiagnosisObj) {
        const diagnosisToUpdate: PatientDiagnosis = {
          ...emptyPatientDiagnosis,
          diagnosis: selectedDiagnosisObj,
        };
        updateSelectedDiagnosis(diagnosisToUpdate);
      }
    },
    [diagnosisSearchResults],
  );
  return (
    <AutoComplete
      endIcon={
        <Search
          fill={primary[100]}
          fillOpacity={1}
          height="25px"
          width="25px"
        />
      }
      fetchOptions={handleDiagnosisSearch}
      hideAvatar
      hideCount
      id={id}
      isDebounced
      isInfiniteScrollable
      isLoading={isDiagnosisCodesLoading}
      marginBottom={marginBottom ?? undefined}
      multiple={multiple}
      onRemove={onRemove}
      onSelect={handleDiagnosisSelect}
      options={diagnosisCodesOptions}
      placeholder={placeholder}
      selected={selected}
      shouldFetchOnListOpen
      showSecondaryText
      title={title}
      totalOptionsCount={100}
    />
  );
};

export default DiagnosisSearch;
